import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { isSmallerSelector } from '~/components/layout/mediaquery.js';
import useTranslations from '~/components/translation/use-translations.ts';
import { selectBrand, selectSportsbookOddsFormatToggleEnabled } from '~/components/brand/ducks/selectors.js';
import { TEXT_KEYS } from './text-keys.js';
import useSportsbookLocation from '~/hooks/use-sportsbook-location';
import { selectIsSportsbookLobbyReady } from '~/components/sportsbook/lobby/sportsbook-root-selectors';
import {
	selectAffiliateFooterTab,
	selectHideSocialMediaLinks,
	selectLicenseLogoSweden,
	selectSponsorIcons
} from './footer-duck';

const SmallerFooter = lazy(() => import('./smaller.js'));
const BiggerFooter = lazy(() => import('./bigger.js'));

const Footer = ({ isLandingPage }) => {
	const authenticated = useSelector(authenticationSelector);
	const smaller = useSelector(isSmallerSelector);
	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);
	const brand = useSelector(selectBrand);
	const isOddsToggleEnabled = useSelector(selectSportsbookOddsFormatToggleEnabled);
	const isSportsbookLobbyReady = useSelector(selectIsSportsbookLobbyReady);
	const { isSportsbookLocation } = useSportsbookLocation();
	const showOddsToggle = isOddsToggleEnabled && isSportsbookLobbyReady && isSportsbookLocation;
	const affiliateFooterTab = useSelector(selectAffiliateFooterTab);
	const LicenseLogoSweden = useSelector(selectLicenseLogoSweden);
	const sponsorIcons = useSelector(selectSponsorIcons);
	const hideSocialMediaLinks = useSelector(selectHideSocialMediaLinks);

	return (
		!isLoadingTranslations && (
			<Suspense fallback={<div />}>
				{smaller ? (
					<SmallerFooter
						LicenseLogoSweden={LicenseLogoSweden}
						authenticated={authenticated}
						hasAffiliateFooterTab={affiliateFooterTab}
						hasSponsorIcons={!!sponsorIcons}
						hideSocialMediaLinks={hideSocialMediaLinks}
						isCanadianFranchise={brand.isCanadianFranchise}
						isDanishFranchise={brand.isDanishFranchise}
						isGermanRegulatedFranchise={brand.isGermanRegulatedFranchise}
						isIndianFranchise={brand.isIndianFranchise}
						isLandingPage={isLandingPage}
						isLyllo={brand.franchiseName === 'SWEDEN (lyllo)'}
						isMaltaRegulatedFranchise={brand.isMaltaRegulatedFranchise}
						isNorway={brand.isNorway}
						isOntarioRegulatedFranchise={brand.isOntarioRegulatedFranchise}
						isPolishFranchise={brand.isPolishFranchise}
						isSwedishRegulatedFranchise={brand.isSwedishRegulatedFranchise}
						locales={brand.locales}
						showNewCookieBanner={brand.showNewCookieBanner}
						siteCode={brand.siteCode}
						franchiseCode={brand.franchiseCode}
						translations={translations}
						showOddsToggle={showOddsToggle}
					/>
				) : (
					<BiggerFooter
						LicenseLogoSweden={LicenseLogoSweden}
						hasAffiliateFooterTab={affiliateFooterTab}
						hasSponsorIcons={!!sponsorIcons}
						hideSocialMediaLinks={hideSocialMediaLinks}
						isCanadianFranchise={brand.isCanadianFranchise}
						isDanishFranchise={brand.isDanishFranchise}
						isGermanRegulatedFranchise={brand.isGermanRegulatedFranchise}
						isIndianFranchise={brand.isIndianFranchise}
						isLandingPage={isLandingPage}
						isLyllo={brand.franchiseName === 'SWEDEN (lyllo)'}
						isMaltaRegulatedFranchise={brand.isMaltaRegulatedFranchise}
						isNorway={brand.isNorway}
						isOntarioRegulatedFranchise={brand.isOntarioRegulatedFranchise}
						isPolishFranchise={brand.isPolishFranchise}
						isSwedishRegulatedFranchise={brand.isSwedishRegulatedFranchise}
						locales={brand.locales}
						franchiseCode={brand.franchiseCode}
						showNewCookieBanner={brand.showNewCookieBanner}
						siteCode={brand.siteCode}
						translations={translations}
						showOddsToggle={showOddsToggle}
					/>
				)}
			</Suspense>
		)
	);
};

export default Footer;
